import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import CustomSelect from "./common/CustomSelect";
import { useNotification } from "../context/NotificationContext";

function TaskForm({
  onSave,
  onClose,
  initial = null,
  users = [],
  currentUser = null,
  readOnly = false,
  allowMainEditing = null,
  teams = [],
  subTeams = [],
  darkMode = false,
  troubleOnlyMode = false
}) {
  const { showAlert } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryStartDate = searchParams.get("startDate");

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const currentYear = new Date().getFullYear();
  const minDate = `${currentYear - 10}-01-01`;
  const maxDate = `${currentYear + 1}-12-31`;

  const isGlobalAdmin = currentUser?.role === "Global Admin";
  const isOperationalAdmin = currentUser?.role === "Operational Admin";
  const isDeptAdmin = currentUser?.role === "Department Admin" || (currentUser?.adminDeptIds && currentUser.adminDeptIds.length > 0);
  const isUser = currentUser?.role === "User";
  const canEditTroubleFields = troubleOnlyMode || !readOnly;

  const allowedTeamIds = [
    currentUser?.team_id,
    ...(currentUser?.memberships || []).map(m => m.team_id)
  ].filter(Boolean);

  const adminDeptIds = [
    currentUser?.role === "Department Admin" ? currentUser?.department_id : null,
    ...(currentUser?.memberships || [])
      .filter(m => m.is_department_admin || m.role === "Department Admin")
      .map(m => m.department_id)
  ].filter(Boolean);

  const selectableTeams = teams.filter((t) => {
    if (isGlobalAdmin || isOperationalAdmin) return true;
    if (isDeptAdmin) return adminDeptIds.includes(t.department_id) || allowedTeamIds.includes(t.id);
    if (isUser) return allowedTeamIds.includes(t.id);
    return false;
  });

  const isMainEditable =
    allowMainEditing !== null
      ? allowMainEditing
      : isGlobalAdmin ||
        isOperationalAdmin ||
        (isDeptAdmin &&
          (!initial ||
            allowedTeamIds.includes(initial.team_id) ||
            adminDeptIds.includes(teams.find(t => t.id === initial.team_id)?.department_id) ||
            initial.created_by === currentUser?.id)) ||
        (isUser && !initial);

  const canEditTaskNoteAndDescAndPriority =
    !readOnly && (isMainEditable || !!initial);

  const buildInitialAssignees = useCallback(() => {
    if (initial?.assignees && Array.isArray(initial.assignees)) {
      return initial.assignees.length
        ? initial.assignees.map((a) => ({
            name: a.name || a.assignee || "",
            email: a.email || a.assignee_email || "",
            id: a.id || a.assignee_id || ""
          }))
        : [{ name: "", email: "", id: "" }];
    }

    if (initial?.assigned_users && Array.isArray(initial.assigned_users)) {
      return initial.assigned_users.length
        ? initial.assigned_users.map((a) => ({
            name: a.name || a.assignee || "",
            email: a.email || a.assignee_email || "",
            id: a.id || a.assignee_id || ""
          }))
        : [{ name: "", email: "", id: "" }];
    }

    if (initial?.task_assignees && Array.isArray(initial.task_assignees)) {
      const mapped = initial.task_assignees
        .map((a) => ({
          name: a.profiles?.name || a.name || "",
          email: a.profiles?.email || a.email || "",
          id: a.user_id || a.profiles?.id || ""
        }))
        .filter((a) => a.name || a.email || a.id);

      return mapped.length ? mapped : [{ name: "", email: "", id: "" }];
    }

    if (initial?.assignee || initial?.assignee_email || initial?.assignee_id) {
      return [
        {
          name: initial.assignee || initial.assigneeName || "",
          email: initial.assignee_email || initial.assigneeEmail || "",
          id: initial.assignee_id || ""
        }
      ];
    }

    return [{ name: "", email: "", id: "" }];
  }, [initial]);

  const normalizeUserCases = useCallback(() => {
    const source = initial?.user_cases || initial?.task_user_cases || [];

    if (Array.isArray(source) && source.length > 0) {
      return source.map((uc) => ({
        title: uc.title || "",
        description: uc.description || "",
        acceptanceCriteria: uc.acceptanceCriteria || uc.acceptance_criteria || "",
        testCases: uc.testCases || [""],
        files: uc.files || []
      }));
    }

    return [{ title: "", description: "", acceptanceCriteria: "", testCases: [""], files: [] }];
  }, [initial]);



  const [form, setForm] = useState({
    taskType: initial?.task_type || "regular",
    taskNote: initial?.task_note || "",
    userCases: normalizeUserCases(),

    repeatFrequency: initial?.repeat_frequency || "Daily",
    repeatEvery: initial?.repeat_every || 1,
    repeatStartDate:
      initial?.repeat_start_date || queryStartDate || new Date().toLocaleDateString("en-CA"),
    repeatEndCondition: initial?.repeat_end_condition || "end_after",
    repeatOccurrences: initial?.repeat_occurrences || 10,
    repeatEndDate: initial?.repeat_end_date || "",
    repeatDays: initial?.repeat_days || ["Mon", "Tue", "Wed", "Thu", "Fri"],
    loopDurationValue: initial?.loop_duration_value || 1,
    loopDurationUnit: initial?.loop_duration_unit || "days",

    issueTitle: "",
    issueDescription: "",
    issuePriority: "",
    issueFiles: [],

    title: "",
    description: "",
    assignees: buildInitialAssignees(),
    startDate: queryStartDate || new Date().toLocaleDateString("en-CA"),
    dueDate: "",
    priority: "Medium",
    status: "Todo",
    progressStatus: "",
    progressReport: "",
    extensionRequestedDate: "",
    hours: "",
    budget: "",
    attachments: initial?.attachments || [],
    teamId:
      initial?.team_id ||
      ((isDeptAdmin || isUser) ? currentUser?.team_id : ""),
    subTeamId: initial?.sub_team_id || "",
    extendedDueDate1: initial?.extended_due_date_1 || "",
    extendedDueDate2: initial?.extended_due_date_2 || "",
    createNextAfterCompletion: initial?.create_next_after_completion || false,
    activeMonths: initial?.active_months || [],
    skipNonWorkingDays: initial?.skip_non_working_days !== undefined ? initial.skip_non_working_days : true,
  });


  const [showLoopPreview, setShowLoopPreview] = useState(false);
  const [showMonthsSelection, setShowMonthsSelection] = useState(() => {
    return !!(initial?.active_months && initial.active_months.length > 0);
  });
  const [previewMonth, setPreviewMonth] = useState(() => {
    const start = form.repeatStartDate || form.startDate || new Date().toLocaleDateString("en-CA");
    return start.slice(0, 7);
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const titleRef = useRef(null);
  const descRef = useRef(null);
  const progressReportRef = useRef(null);
  const selectionRef = useRef({ name: null, start: 0, end: 0 });

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const dayMap = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  const getNextSelectedWeekday = (fromDate, selectedDay) => {
    const targetDay = dayMap[selectedDay];
    if (targetDay === undefined) return new Date(fromDate);

    const next = new Date(fromDate);
    const diff = (targetDay - next.getDay() + 7) % 7;

    next.setDate(next.getDate() + (diff === 0 ? 7 : diff));
    return next;
  };
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  useLayoutEffect(() => {
    if (selectionRef.current.name === "title" && titleRef.current) {
      titleRef.current.setSelectionRange(
        selectionRef.current.start,
        selectionRef.current.end
      );
      selectionRef.current.name = null;
    } else if (selectionRef.current.name === "description" && descRef.current) {
      descRef.current.setSelectionRange(
        selectionRef.current.start,
        selectionRef.current.end
      );
      selectionRef.current.name = null;
    }
  });

  useLayoutEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = "auto";
      descRef.current.style.height = `${descRef.current.scrollHeight}px`;
    }
  }, [form.description]);

  useLayoutEffect(() => {
    if (progressReportRef.current) {
      progressReportRef.current.style.height = "auto";
      progressReportRef.current.style.height = `${progressReportRef.current.scrollHeight}px`;
    }
  }, [form.progressReport]);

  useEffect(() => {
    if (initial) {
      const initialAssignees = buildInitialAssignees();


      setForm((prev) => ({
        ...prev,
        taskType: initial.task_type || "regular",
        taskNote: initial.task_note || "",
        userCases: normalizeUserCases(),
        repeatFrequency: initial.repeat_frequency || "Daily",
        repeatEvery: initial.repeat_every || 1,
        repeatStartDate:
          initial.repeat_start_date ||
          initial.start_date ||
          new Date().toLocaleDateString("en-CA"),
        repeatEndCondition: initial.repeat_end_condition || "end_after",
        repeatOccurrences: initial.repeat_occurrences || 10,
        repeatEndDate: initial.repeat_end_date || "",
        repeatDays: initial.repeat_days || ["Mon", "Tue", "Wed", "Thu", "Fri"],
        loopDurationValue: initial.loop_duration_value || 1,
        loopDurationUnit: initial.loop_duration_unit || "days",

        title: initial.title || "",
        description: initial.description || "",
        assignees: initialAssignees,
        startDate: initial.startDate
          ? typeof initial.startDate === "string"
            ? initial.startDate.slice(0, 10)
            : new Date(initial.startDate).toISOString().slice(0, 10)
          : initial.start_date || "",
        dueDate: initial.dueDate
          ? typeof initial.dueDate === "string"
            ? initial.dueDate.slice(0, 10)
            : new Date(initial.dueDate).toISOString().slice(0, 10)
          : initial.due_date || "",
        priority: initial.priority || "Medium",
        status: initial.status !== undefined && initial.status !== null ? initial.status : "Todo",
        budget: initial.budget || "",
        progressStatus: troubleOnlyMode ? "Trouble" : initial.progress_status || "",
        progressReport: initial.progress_report || "",
        extensionRequestedDate:
          initial.extension_requested_date ||
          initial.requested_extension_date ||
          "",
        hours: initial.hours || "",
        attachments:
          initial.attachments && initial.attachments.length > 0
            ? initial.attachments
            : initial.attachment_url
              ? [{ name: "Attachment", url: initial.attachment_url }]
              : [],
        teamId: initial.team_id || "",
        subTeamId: initial.sub_team_id || "",
        extendedDueDate1:
          initial.extended_due_date_1 ||
          initial.extended_due_dates?.[0] ||
          "",
        extendedDueDate2:
          initial.extended_due_date_2 ||
          initial.extended_due_dates?.[1] ||
          "",
        createNextAfterCompletion: initial.create_next_after_completion || false,
        activeMonths: initial.active_months || [],
        skipNonWorkingDays: initial.skip_non_working_days !== undefined ? initial.skip_non_working_days : true
      }));



      if (initial.active_months && initial.active_months.length > 0) {
        setShowMonthsSelection(true);
      } else {
        setShowMonthsSelection(false);
      }
    }
  }, [initial, troubleOnlyMode, buildInitialAssignees, normalizeUserCases]);

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "12px",
    border: darkMode ? "1px solid #374151" : "1px solid #e2e8f0",
    backgroundColor: darkMode ? "#111827" : "white",
    color: darkMode ? "white" : "inherit",
    outline: "none",
    boxSizing: "border-box"
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: 700,
    fontSize: "12px",
    color: darkMode ? "#d1d5db" : "#475569",
    textTransform: "uppercase"
  };

  const sectionStyle = {
    background: darkMode ? "#1f2937" : "white",
    padding: "18px",
    borderRadius: "12px",
    border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb"
  };

  const sectionTitleStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px"
  };

  const sectionBarStyle = {
    width: "4px",
    height: "16px",
    background: "#76AB3F",
    borderRadius: "2px"
  };

  const sectionHeadingStyle = {
    margin: 0,
    fontSize: "14px",
    fontWeight: 800,
    color: darkMode ? "#f9fafb" : "#1f2937",
    textTransform: "uppercase"
  };






  const navBtnStyle = {
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    fontSize: "26px",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const navTextBtnStyle = {
    height: "46px",
    minWidth: "110px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    padding: "0 18px",
    fontWeight: 900,
    cursor: "pointer",
    color: "#111827",
  };

  const syncPrimaryAssignee = (assignees) => {
    const cleanAssignees = assignees.filter((a) => a.name || a.email || a.id);

    return {
      assignees:
        cleanAssignees.length > 0
          ? cleanAssignees
          : [{ name: "", email: "", id: "" }],
    };
  };

  const handleAssigneeChange = (index, field, value) => {
    setForm((prev) => {
      const nextAssignees = [...prev.assignees];
      const currentAssignee =
        nextAssignees[index] || { name: "", email: "", id: "" };

      let updatedAssignee = {
        ...currentAssignee,
        [field]: value
      };

      const matchedUser = users.find((u) => {
        if (field === "name") return u.name === value || u.email === value;
        if (field === "email") return u.email === value || u.name === value;
        return false;
      });

      let teamId = prev.teamId;
      let subTeamId = prev.subTeamId;

      if (matchedUser) {
        updatedAssignee = {
          name: matchedUser.name || "",
          email: matchedUser.email || "",
          id: matchedUser.id || ""
        };

        if (index === 0) {
          teamId = matchedUser.team_id || prev.teamId;
          subTeamId =
            matchedUser.sub_team_ids && matchedUser.sub_team_ids.length > 0
              ? matchedUser.sub_team_ids[0]
              : "";
        }
      } else {
        updatedAssignee.id = "";
      }

      nextAssignees[index] = updatedAssignee;

      return {
        ...prev,
        ...syncPrimaryAssignee(nextAssignees),
        teamId,
        subTeamId
      };
    });
  };

  const addAssigneeRow = () => {
    setForm((prev) => ({
      ...prev,
      assignees: [...prev.assignees, { name: "", email: "", id: "" }]
    }));
  };

  const removeAssigneeRow = (index) => {
    setForm((prev) => {
      const nextAssignees = prev.assignees.filter((_, i) => i !== index);
      return {
        ...prev,
        ...syncPrimaryAssignee(nextAssignees)
      };
    });
  };



  const toggleRepeatDay = (day) => {
    if (readOnly || !isMainEditable) return;

    setForm((prev) => ({
      ...prev,
      repeatDays: prev.repeatDays.includes(day)
        ? prev.repeatDays.filter((d) => d !== day)
        : [...prev.repeatDays, day]
    }));
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "title" || name === "description" || name === "taskNote") {
      selectionRef.current = {
        name,
        start: e.target.selectionStart,
        end: e.target.selectionEnd
      };

      if (value.length > 0) {
        value = value.charAt(0).toUpperCase() + value.slice(1);
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const removeAttachment = (index) => {
    setForm((prev) => {
      const newAttachments = [...prev.attachments];
      newAttachments.splice(index, 1);
      return { ...prev, attachments: newAttachments };
    });
  };

  const getRepeatUnitLabel = () => {
    if (form.repeatFrequency === "Daily") return "day(s)";
    if (form.repeatFrequency === "Weekly") return "week(s)";
    if (form.repeatFrequency === "Monthly") return "month(s)";
    if (form.repeatFrequency === "Monthly by Date") return "month(s)";
    if (form.repeatFrequency === "Yearly") return "year(s)";
    return "day(s)";
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploading) return;

    setUploading(true);

    let finalAttachments = [...(form.attachments || [])];

    try {
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
          const fileName = `${Date.now()}_${safeName}`;

          const { error: uploadError } = await supabase.storage
            .from("attachments")
            .upload(fileName, file);

          if (uploadError) throw uploadError;

          const { data: publicUrlData } = supabase.storage
            .from("attachments")
            .getPublicUrl(fileName);

          finalAttachments.push({
            name: file.name,
            url: publicUrlData.publicUrl
          });
        }
      }

      if (troubleOnlyMode || form.progressStatus === "Trouble") {
        if (!form.progressReport.trim()) {
          showAlert("Please enter trouble reason.");
          setUploading(false);
          return;
        }

        if (!form.extensionRequestedDate) {
          showAlert("Please select requested extension date.");
          setUploading(false);
          return;
        }
      }

      if (form.taskType === "loop" && !form.repeatStartDate) {
        showAlert("Please select repeat start date.");
        setUploading(false);
        return;
      }

      if (!form.description || !form.description.trim()) {
        showAlert("Description is required.");
        setUploading(false);
        return;
      }

      const hasEmptyAssignee = form.assignees.some(a => !a.name || !a.name.trim() || !a.email || !a.email.trim());
      if (form.assignees.length === 0 || hasEmptyAssignee) {
        showAlert("Assignee Name and Assignee Email are required.");
        setUploading(false);
        return;
      }

      if (
        form.taskType === "loop" &&
        form.repeatEndCondition === "end_on" &&
        !form.repeatEndDate
      ) {
        showAlert("Please select repeat end date.");
        setUploading(false);
        return;
      }

      const cleanAssignees = (form.assignees || [])
        .filter((a) => a.name || a.email || a.id)
        .map((a) => ({
          name: a.name || "",
          email: a.email || "",
          id: a.id || ""
        }));

      const cleanUserCases = (form.userCases || [])
        .filter(
          (uc) =>
            uc.title ||
            uc.description ||
            uc.acceptanceCriteria ||
            (uc.testCases || []).some((tc) => tc) ||
            (uc.files || []).length
        )
        .map((uc) => ({
          title: uc.title || "",
          description: uc.description || "",
          acceptanceCriteria: uc.acceptanceCriteria || "",
          testCases: (uc.testCases || []).filter(Boolean),
          files: uc.files || [],
        }));

      const calculatedLoopDates = getLoopPreviewDates();
      const calculatedLoopInstances = calculatedLoopDates.map((startDate, index) => ({
        id: index + 1,
        startDate,
        endDate: calculateLoopDueDate(startDate),
      }));

      const finalLoopEndDate =
        calculatedLoopInstances[calculatedLoopInstances.length - 1]?.endDate || null;

      const payload = {
        title: (form.title || "").trim() || "Untitled",
        task_note: (form.taskNote || "").trim() || null,
        description: (form.description || "").trim() || null,

        user_cases: cleanUserCases,

        task_type: form.taskType,
        repeat_frequency: form.taskType === "loop" ? form.repeatFrequency : null,
        repeat_every:
          form.taskType === "loop" ? Number(form.repeatEvery || 1) : 1,
        repeat_start_date:
          form.taskType === "loop" ? form.repeatStartDate || null : null,
        repeat_end_date:
          form.taskType === "loop"
            ? finalLoopEndDate
            : null,
        repeat_occurrences:
          form.taskType === "loop"
            ? calculatedLoopInstances.length
            : null,
        active_months:
          form.taskType === "loop"
            ? form.activeMonths || []
            : [],
        repeat_days: form.taskType === "loop" ? form.repeatDays || [] : [],
        loopDurationValue:
          form.taskType === "loop" ? Number(form.loopDurationValue || 1) : null,

        loopDurationUnit:
          form.taskType === "loop" ? form.loopDurationUnit : null,

        loop_duration_value:
          form.taskType === "loop" ? Number(form.loopDurationValue || 1) : null,

        loop_duration_unit:
          form.taskType === "loop" ? form.loopDurationUnit : null,

        create_next_after_completion:
          form.taskType === "loop" ? form.createNextAfterCompletion : false,
        skip_non_working_days:
          form.taskType === "loop" ? form.skipNonWorkingDays : false,
        repeat_end_condition:
          form.taskType === "loop" ? form.repeatEndCondition : null,

        assignees: cleanAssignees,
        assigned_users: cleanAssignees,

        start_date:
          form.taskType === "loop"
            ? form.repeatStartDate || form.startDate
            : form.startDate,
        due_date:
          form.taskType === "regular"
            ? form.dueDate
            : calculateLoopDueDate(form.repeatStartDate || form.startDate),
        priority: form.priority || "Medium",
        status: form.status || "Todo",
        budget: form.budget ? parseInt(form.budget, 10) : null,
        hours: form.hours ? parseFloat(form.hours) : 0,
        progress_status: troubleOnlyMode ? "Trouble" : form.progressStatus || null,
        progress_report: form.progressReport || null,
        extension_requested_date:
          troubleOnlyMode || form.progressStatus === "Trouble"
            ? form.extensionRequestedDate || null
            : null,
        team_id: form.teamId || null,
        sub_team_id: form.subTeamId || null,
        attachments: finalAttachments,
        extended_due_date_1: form.extendedDueDate1 || null,
        extended_due_date_2: form.extendedDueDate2 || null,
        extended_due_dates: [
          form.extendedDueDate1,
          form.extendedDueDate2
        ].filter(Boolean),
        issue_title: form.issueTitle || null,
        issue_description: form.issueDescription || null,
        issue_priority: form.priority || "Medium",
        issue_files: form.issueFiles || [],
        updated_at: new Date().toISOString()
      };

      if (onSave) {
        await onSave(payload);
        if (!initial) {
          if (queryStartDate) {
            navigate("/calendar");
          } else {
            navigate("/tasks");
          }
        }
      }
    } catch (err) {
      console.error("Error saving task:", err);
      showAlert("Error saving: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const toIsoDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  };

  const calculateLoopDueDate = (startDate) => {
    if (!startDate) return null;

    const due = new Date(`${startDate}T00:00:00`);
    const value = Number(form.loopDurationValue || 1);

    const daysToAdd =
      form.loopDurationUnit === "weeks"
        ? value * 7 - 1
        : value - 1;

    due.setDate(due.getDate() + Math.max(daysToAdd, 0));
    return toIsoDate(due);
  };

  const formatPreviewDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const shouldSkipDate = (date) => {
    const skipNonWorkingDays = form.skipNonWorkingDays;

    if (skipNonWorkingDays && isWeekend(date)) return true;

    if (form.activeMonths?.length > 0) {
      const monthNumber = date.getMonth() + 1;
      if (!form.activeMonths.includes(monthNumber)) return true;
    }

    return false;
  };

  const getLoopPreviewDates = () => {
    const start = form.repeatStartDate || form.startDate;
    if (!start) return [];

    const every = Number(form.repeatEvery || 1);
    const maxSafetyLimit = 500;
    const dates = [];

    let current = new Date(`${start}T00:00:00`);

    const endDate =
      form.repeatEndCondition === "end_on" && form.repeatEndDate
        ? new Date(`${form.repeatEndDate}T00:00:00`)
        : null;

    const targetOccurrences =
      form.repeatEndCondition === "end_after"
        ? Number(form.repeatOccurrences || 1)
        : maxSafetyLimit;

    while (dates.length < targetOccurrences && dates.length < maxSafetyLimit) {
      if (endDate && current > endDate) break;

      if (!shouldSkipDate(current)) {
        dates.push(toIsoDate(current));
      }

      const next = new Date(current);

      if (form.repeatFrequency === "Daily") {
        next.setDate(next.getDate() + every);
      } else if (form.repeatFrequency === "Weekly") {
        next.setDate(next.getDate() + every * 7);
      } else if (
        form.repeatFrequency === "Monthly" ||
        form.repeatFrequency === "Monthly by Date"
      ) {
        next.setMonth(next.getMonth() + every);
      } else if (form.repeatFrequency === "Yearly") {
        next.setFullYear(next.getFullYear() + every);
      }

      current = next;
    }

    return dates;
  };

  const buildPreviewCalendar = () => {
    const [year, month] = previewMonth.split("-").map(Number);
    const first = new Date(year, month - 1, 1);
    const startCell = new Date(first);
    startCell.setDate(first.getDate() - first.getDay());

    const previewDates = getLoopPreviewDates();

    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(startCell);
      d.setDate(startCell.getDate() + i);

      const iso = toIsoDate(d);

      return {
        iso,
        day: d.getDate(),
        inMonth: d.getMonth() === month - 1,
        hasTask: previewDates.includes(iso),
      };
    });
  };

  const movePreviewMonth = (step) => {
    const [year, month] = previewMonth.split("-").map(Number);
    const d = new Date(year, month - 1 + step, 1);
    setPreviewMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const goPreviewToday = () => {
    const today = new Date();
    setPreviewMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  };

  const previewMonthLabel = new Date(`${previewMonth}-01T00:00:00`).toLocaleDateString(
    "en-US",
    {
      month: "long",
      year: "numeric",
    }
  );

  const getLoopPreviewInstances = () => {
    return getLoopPreviewDates().map((startDate, index) => ({
      id: index + 1,
      startDate,
      endDate: calculateLoopDueDate(startDate),
    }));
  };

  const getTasksForPreviewDate = (iso) => {
    return getLoopPreviewInstances().filter((item) => {
      return iso >= item.startDate && iso <= item.endDate;
    });
  };

  const getNextOccurrenceText = () => {
    const baseDate = form.repeatStartDate || form.startDate;
    if (!baseDate) return "Select start date";

    const every = Number(form.repeatEvery || 1);
    let date = new Date(`${baseDate}T00:00:00`);

    const incrementDate = (d) => {
      const next = new Date(d);
      if (form.repeatFrequency === "Daily") {
        next.setDate(next.getDate() + every);
      } else if (form.repeatFrequency === "Weekly") {
        const selectedDay =
          Array.isArray(form.repeatDays) && form.repeatDays.length
            ? form.repeatDays[0]
            : null;

        if (selectedDay) {
          return getNextSelectedWeekday(next, selectedDay);
        }
        next.setDate(next.getDate() + every * 7);
      } else if (
        form.repeatFrequency === "Monthly" ||
        form.repeatFrequency === "Monthly by Date"
      ) {
        next.setMonth(next.getMonth() + every);
      } else if (form.repeatFrequency === "Yearly") {
        next.setFullYear(next.getFullYear() + every);
      }
      return next;
    };

    date = incrementDate(date);

    let safety = 0;
    while (shouldSkipDate(date) && safety < 500) {
      date = incrementDate(date);
      safety++;
    }

    if (safety >= 500) {
      return "No valid next occurrence found";
    }

    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  useEffect(() => {
    if (form.taskType !== "loop") return;

    if (form.repeatEndCondition === "end_on") {
      const dates = getLoopPreviewDates();
      setForm((prev) => {
        if (prev.repeatOccurrences !== dates.length) {
          return { ...prev, repeatOccurrences: dates.length };
        }
        return prev;
      });
    } else if (form.repeatEndCondition === "end_after") {
      const dates = getLoopPreviewDates();
      if (dates.length > 0) {
        const calculatedInstances = dates.map((startDate) => ({
          startDate,
          endDate: calculateLoopDueDate(startDate),
        }));
        const calculatedEndDate = calculatedInstances[calculatedInstances.length - 1]?.endDate || "";
        setForm((prev) => {
          if (prev.repeatEndDate !== calculatedEndDate) {
            return { ...prev, repeatEndDate: calculatedEndDate };
          }
          return prev;
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.taskType,
    form.repeatEndCondition,
    form.repeatStartDate,
    form.startDate,
    form.repeatEndDate,
    form.repeatOccurrences,
    form.repeatFrequency,
    form.repeatEvery,
    form.activeMonths,
    form.skipNonWorkingDays,
    form.loopDurationValue,
    form.loopDurationUnit,
  ]);

  const previewInstances = getLoopPreviewInstances();

  return (
    <div
      className="task-form-page"
      style={{
        width: "100%",
        minHeight: "100vh",
        background: darkMode ? "#111827" : "#f8fafc",
        padding: "24px",
        boxSizing: "border-box"
      }}
    >
      <div
        className="task-form-card"
        style={{
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxWidth: "1180px",
          width: "100%",
          margin: "0 auto",
          backgroundColor: darkMode ? "#1f2937" : "white",
          borderRadius: "18px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          border: darkMode
            ? "1px solid #374151"
            : "1px solid #e5e7eb"
        }}
      >


        <form
          onSubmit={handleSubmit}
          style={{
            padding: "24px",
            overflowY: "auto",
            background: darkMode ? "#111827" : "#f8fafc",
            display: "flex",
            flexDirection: "column",
            gap: "18px"
          }}
        >


          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <div style={sectionBarStyle} />
              <h4 style={sectionHeadingStyle}>Task Details</h4>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
              <div className="form-group">
                <label style={labelStyle}>Task Title *</label>
                <input
                  ref={titleRef}
                  name="title"
                  placeholder="Enter task title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  disabled={readOnly || !isMainEditable}
                  style={inputStyle}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Task Note</label>
                <div style={{ position: "relative" }}>
                  <textarea
                    name="taskNote"
                    placeholder="Enter task note"
                    value={form.taskNote}
                    onChange={handleChange}
                    disabled={!canEditTaskNoteAndDescAndPriority}
                    style={{
                      ...inputStyle,
                      minHeight: "60px",
                      resize: "vertical",
                      paddingRight: "40px"
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "12px",
                      right: "12px",
                      pointerEvents: "none",
                      color: darkMode ? "#9ca3af" : "#64748b"
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label style={labelStyle}>Description <span style={{ color: "#ef4444" }}>*</span></label>
                <div style={{ position: "relative" }}>
                  <textarea
                    ref={descRef}
                    name="description"
                    placeholder="Enter task description"
                    value={form.description}
                    onChange={handleChange}
                    required
                    disabled={!canEditTaskNoteAndDescAndPriority}
                    style={{
                      ...inputStyle,
                      minHeight: "80px",
                      resize: "vertical",
                      paddingRight: "40px"
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "12px",
                      right: "12px",
                      pointerEvents: "none",
                      color: darkMode ? "#9ca3af" : "#64748b"
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <div style={sectionBarStyle} />
              <h4 style={sectionHeadingStyle}>Operational Details</h4>
            </div>

            <div className="task-form-operational-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div className="form-group" style={{ gridColumn: "span 2" }}>
                <div className="task-form-assignee-header" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 32px", gap: "16px", alignItems: "end" }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Assignee Name *</label>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Assignee Email *</label>
                  <div style={{ width: "32px" }}></div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
                  {form.assignees.map((assignee, index) => (
                    <div
                      key={`assignee-${index}`}
                      className="task-form-assignee-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 32px",
                        gap: "16px",
                        alignItems: "center"
                      }}
                    >
                      <input
                        id={`assignee-name-${index}`}
                        name={`assignee-name-${index}`}
                        value={assignee.name}
                        onChange={(e) =>
                          handleAssigneeChange(index, "name", e.target.value)
                        }
                        list="assignee-list"
                        disabled={readOnly || !isMainEditable}
                        placeholder="Enter assignee name"
                        style={inputStyle}
                      />

                      <input
                        id={`assignee-email-${index}`}
                        name={`assignee-email-${index}`}
                        value={assignee.email}
                        onChange={(e) =>
                          handleAssigneeChange(index, "email", e.target.value)
                        }
                        list="assignee-email-list"
                        disabled={readOnly || !isMainEditable}
                        placeholder="Enter assignee email"
                        style={inputStyle}
                      />

                      {index === 0 ? (
                        !readOnly && isMainEditable && (
                          <button
                            type="button"
                            onClick={addAssigneeRow}
                            style={{
                              background: "#76AB3F",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              fontSize: "18px",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(118, 171, 63, 0.3)"
                            }}
                            title="Add another assignee"
                          >
                            +
                          </button>
                        )
                      ) : (
                        !readOnly && isMainEditable && (
                          <button
                            type="button"
                            onClick={() => removeAssigneeRow(index)}
                            style={{
                              background: "#fee2e2",
                              border: "none",
                              color: "#ef4444",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              fontSize: "18px",
                              fontWeight: "bold"
                            }}
                            title="Remove assignee"
                          >
                            ×
                          </button>
                        )
                      )}
                    </div>
                  ))}
                </div>

                <datalist id="assignee-list">
                  {users.map((u) => (
                    <option key={`name-${u.id}`} value={u.name || u.email} />
                  ))}
                </datalist>

                <datalist id="assignee-email-list">
                  {users.map((u) => (
                    <option key={`email-${u.id}`} value={u.email || u.name} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label style={labelStyle}>Team</label>
                <CustomSelect
                  options={selectableTeams.map((t) => ({ value: t.id, label: t.name }))}
                  value={form.teamId}
                  onChange={(val) =>
                    setForm({ ...form, teamId: val, subTeamId: "" })
                  }
                  disabled={
                    readOnly ||
                    !isMainEditable ||
                    (selectableTeams.length <= 1)
                  }
                  darkMode={darkMode}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Sub Team</label>
                <CustomSelect
                  options={subTeams
                    .filter((st) => st.team_id === form.teamId)
                    .map((st) => ({ value: st.id, label: st.name }))}
                  value={form.subTeamId}
                  onChange={(val) => setForm({ ...form, subTeamId: val })}
                  disabled={
                    readOnly ||
                    !isMainEditable ||
                    !form.teamId
                  }
                  darkMode={darkMode}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Budget ($)</label>
                <input
                  type="number"
                  name="budget"
                  placeholder="Enter budget"
                  value={form.budget}
                  onChange={handleChange}
                  disabled={readOnly || !isMainEditable}
                  style={inputStyle}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Estimated Hours</label>
                <input
                  type="number"
                  name="hours"
                  placeholder="Enter estimated hours"
                  value={form.hours}
                  onChange={handleChange}
                  disabled={readOnly || !isMainEditable}
                  style={inputStyle}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Priority</label>
                <CustomSelect
                  options={["Low", "Medium", "High"]}
                  value={form.priority}
                  onChange={(val) => setForm({ ...form, priority: val })}
                  disabled={!canEditTaskNoteAndDescAndPriority}
                  darkMode={darkMode}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Status</label>
                <CustomSelect
                  options={["Todo", "In Progress", "Completed"]}
                  value={form.status}
                  onChange={(val) => setForm({ ...form, status: val })}
                  disabled={readOnly || !isMainEditable}
                  darkMode={darkMode}
                />
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#76AB3F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <h4 style={{ ...sectionHeadingStyle, textTransform: "uppercase", fontSize: "14px", fontWeight: 800 }}>Schedule</h4>
                <p style={{ margin: "2px 0 0", fontSize: "11px", color: darkMode ? "#9ca3af" : "#64748b", fontWeight: 500 }}>
                  Configure when and how this task should occur.
                </p>
              </div>
            </div>

            {form.taskType === "regular" ? (
              <div className="task-form-regular-schedule-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginTop: "16px" }}>
                <div className="form-group">
                  <label style={labelStyle}>Task Type for Scheduling <span style={{ color: "#ef4444" }}>*</span></label>
                  <select
                    value={form.taskType}
                    onChange={(e) => {
                      if (!readOnly && isMainEditable) {
                        setForm({ ...form, taskType: e.target.value });
                      }
                    }}
                    disabled={readOnly || !isMainEditable}
                    style={{
                      ...inputStyle,
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${darkMode ? "%23ffffff" : "%2364748b"}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      backgroundSize: "14px",
                      paddingRight: "32px",
                      color: darkMode ? "#ffffff" : "#111827"
                    }}
                  >
                    <option value="regular">Regular Task</option>
                    <option value="loop">Recurring Task</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={labelStyle}>Start Date <span style={{ color: "#ef4444" }}>*</span></label>
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => {
                        const updated = { ...prev, startDate: val, repeatStartDate: val };
                        if (updated.dueDate && updated.dueDate < val) updated.dueDate = val;
                        if (updated.repeatEndDate && updated.repeatEndDate < val) updated.repeatEndDate = val;
                        return updated;
                      });
                    }}
                    min={minDate}
                    max={maxDate}
                    required
                    disabled={readOnly || !isMainEditable}
                    style={inputStyle}
                  />
                </div>

                <div className="form-group">
                  <label style={labelStyle}>End Date <span style={{ color: "#ef4444" }}>*</span></label>
                  <input
                    type="date"
                    name="dueDate"
                    value={form.dueDate}
                    onChange={handleChange}
                    min={form.startDate || minDate}
                    max={maxDate}
                    required={form.taskType === "regular"}
                    disabled={readOnly || !isMainEditable}
                    style={inputStyle}
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}>
                {/* Row 1: 4 columns */}
                <div className="task-form-loop-row1-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "16px" }}>
                  <div className="form-group">
                    <label style={labelStyle}>Task Type for Scheduling <span style={{ color: "#ef4444" }}>*</span></label>
                    <select
                      value={form.taskType}
                      onChange={(e) => {
                        if (!readOnly && isMainEditable) {
                          setForm({ ...form, taskType: e.target.value });
                        }
                      }}
                      disabled={readOnly || !isMainEditable}
                      style={{
                        ...inputStyle,
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${darkMode ? "%23ffffff" : "%2364748b"}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        backgroundSize: "14px",
                        paddingRight: "32px",
                        color: darkMode ? "#ffffff" : "#111827"
                      }}
                    >
                      <option value="regular">Regular Task</option>
                      <option value="loop">Recurring Task</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={labelStyle}>Start Date <span style={{ color: "#ef4444" }}>*</span></label>
                    <input
                      type="date"
                      name="repeatStartDate"
                      value={form.repeatStartDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setForm((prev) => {
                          const updated = { ...prev, repeatStartDate: val, startDate: val };
                          if (updated.dueDate && updated.dueDate < val) updated.dueDate = val;
                          if (updated.repeatEndDate && updated.repeatEndDate < val) updated.repeatEndDate = val;
                          return updated;
                        });
                      }}
                      min={minDate}
                      max={maxDate}
                      required={form.taskType === "loop"}
                      disabled={readOnly || !isMainEditable}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group">
                    <label style={labelStyle}>End Type <span style={{ color: "#ef4444" }}>*</span></label>
                    <CustomSelect
                      options={[
                        { value: "never", label: "Never" },
                        { value: "end_after", label: "End after" },
                        { value: "end_on", label: "End on" }
                      ]}
                      value={form.repeatEndCondition}
                      onChange={(val) => setForm({ ...form, repeatEndCondition: val })}
                      disabled={readOnly || !isMainEditable}
                      darkMode={darkMode}
                    />
                  </div>

                  {form.repeatEndCondition !== "never" ? (
                    <div className="form-group">
                      {form.repeatEndCondition === "end_after" ? (
                        <>
                          <label style={labelStyle}>No. of Times / Occurrences <span style={{ color: "#ef4444" }}>*</span></label>
                          <input
                            type="number"
                            name="repeatOccurrences"
                            placeholder="Enter occurrences"
                            value={form.repeatOccurrences}
                            onChange={handleChange}
                            min="1"
                            disabled={readOnly || !isMainEditable}
                            style={inputStyle}
                          />
                        </>
                      ) : (
                        <>
                          <label style={labelStyle}>End Date <span style={{ color: "#ef4444" }}>*</span></label>
                          <input
                            type="date"
                            name="repeatEndDate"
                            value={form.repeatEndDate}
                            onChange={handleChange}
                            min={form.repeatStartDate || minDate}
                            max={maxDate}
                            disabled={readOnly || !isMainEditable}
                            placeholder="mm/dd/yyyy"
                            style={inputStyle}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>

                {/* Row 2: REPEAT EVERY, INTERVAL, REPEAT ON, TASK DURATION */}
                <div className="task-form-loop-row2-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 2.5fr 1.8fr", gap: "16px", alignItems: "end" }}>
                  <div className="form-group">
                    <label style={labelStyle}>Repeat Every <span style={{ color: "#ef4444" }}>*</span></label>
                    <CustomSelect
                      options={[
                        { value: "Daily", label: "Daily" },
                        { value: "Weekly", label: "Weekly" },
                        { value: "Monthly", label: "Monthly" },
                        { value: "Yearly", label: "Yearly" }
                      ]}
                      value={form.repeatFrequency}
                      onChange={(val) =>
                        setForm({
                          ...form,
                          repeatFrequency: val,
                          repeatDays:
                            val === "Weekly"
                              ? form.repeatDays.length
                                ? form.repeatDays
                                : ["Mon"]
                              : form.repeatDays
                        })
                      }
                      disabled={readOnly || !isMainEditable}
                      darkMode={darkMode}
                    />
                  </div>

                  <div className="form-group">
                    <label style={labelStyle}>Interval <span style={{ color: "#ef4444" }}>*</span></label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <input
                        type="number"
                        name="repeatEvery"
                        placeholder="Enter interval"
                        value={form.repeatEvery}
                        onChange={handleChange}
                        min="1"
                        disabled={readOnly || !isMainEditable}
                        style={{ ...inputStyle, paddingRight: "65px" }}
                      />
                      <span style={{ position: "absolute", right: "12px", color: darkMode ? "#9ca3af" : "#64748b", fontWeight: 600, fontSize: "13px" }}>
                        time(s)
                      </span>
                    </div>
                  </div>

                  <div className="form-group" style={{ gridColumn: "span 1" }}>
                    <label style={labelStyle}>Repeat On <span style={{ color: "#ef4444" }}>*</span></label>
                    <div className="task-form-repeat-days-row" style={{ display: "flex", gap: "8px", width: "100%", height: "46px", alignItems: "center" }}>
                      {weekDays.map((day) => {
                        const active = form.repeatDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleRepeatDay(day)}
                            disabled={readOnly || !isMainEditable}
                            style={{
                              height: "46px",
                              borderRadius: "10px",
                              border: active
                                ? "1.5px solid #76AB3F"
                                : darkMode
                                  ? "1px solid #374151"
                                  : "1px solid #cbd5e1",
                              background: active
                                ? (darkMode ? "rgba(118, 171, 63, 0.15)" : "#f1f8ea")
                                : (darkMode ? "#111827" : "white"),
                              color: active
                                ? "#76AB3F"
                                : (darkMode ? "#9ca3af" : "#64748b"),
                              fontWeight: 700,
                              cursor: readOnly || !isMainEditable ? "default" : "pointer",
                              fontSize: "13px",
                              flex: 1,
                              textAlign: "center",
                              transition: "all 0.2s",
                              boxSizing: "border-box"
                            }}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={labelStyle}>Task Duration <span style={{ color: "#ef4444" }}>*</span></label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: "10px" }}>
                      <input
                        type="number"
                        name="loopDurationValue"
                        value={form.loopDurationValue}
                        onChange={handleChange}
                        min="1"
                        disabled={readOnly || !isMainEditable}
                        style={inputStyle}
                      />

                      <select
                        value={form.loopDurationUnit}
                        onChange={(e) => setForm({ ...form, loopDurationUnit: e.target.value })}
                        disabled={readOnly || !isMainEditable}
                        style={{
                          ...inputStyle,
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${darkMode ? "%23ffffff" : "%2364748b"}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 12px center",
                          backgroundSize: "14px",
                          paddingRight: "32px",
                          color: darkMode ? "#ffffff" : "#111827"
                        }}
                      >
                        <option value="days">Day(s)</option>
                        <option value="weeks">Week(s)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Row 3: Banner next occurrence */}
                {previewInstances.length === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      background: "#fef2f2",
                      border: "1px solid #fee2e2",
                      borderRadius: "12px",
                      padding: "14px 18px",
                      color: "#991b1b",
                      gap: "6px",
                      marginTop: "8px"
                    }}
                  >
                    <div style={{ fontSize: "14px", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                      ⚠️ Configuration Error
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 500 }}>
                      {form.repeatEndCondition === "end_on" && form.repeatEndDate && new Date(form.repeatStartDate) > new Date(form.repeatEndDate)
                        ? "Start date cannot be after the repeat end date."
                        : "No occurrences can be generated with the current settings. Please verify dates, repeat frequency, active months, and weekend skip settings."}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#f1f8ea",
                      border: "1px solid #d7ebc8",
                      borderRadius: "12px",
                      padding: "12px 18px",
                      color: "#315d1a",
                      marginTop: "8px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span style={{ fontSize: "13px", fontWeight: 700 }}>
                        Next occurrence will be on <strong style={{ fontWeight: 900 }}>{getNextOccurrenceText()}</strong>
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const start = form.repeatStartDate || form.startDate || new Date().toLocaleDateString("en-CA");
                        setPreviewMonth(start.slice(0, 7));
                        setShowLoopPreview(true);
                      }}
                      style={{
                        background: "#ffffff",
                        border: "2px solid #76AB3F",
                        borderRadius: "10px",
                        padding: "8px 16px",
                        fontWeight: 800,
                        color: "#76AB3F",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        textTransform: "uppercase",
                        fontSize: "12px",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f1f8ea"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#ffffff"}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      Preview Schedule
                    </button>
                  </div>
                )}

                {/* Divider & Months Select Row */}
                <div style={{ position: "relative", textAlign: "center", margin: "16px 0" }}>
                  <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: "1px", background: darkMode ? "#374151" : "#e2e8f0", zIndex: 1 }} />
                  <span style={{ position: "relative", background: darkMode ? "#1f2937" : "white", padding: "0 16px", zIndex: 2, fontSize: "12px", fontWeight: 700, color: darkMode ? "#9ca3af" : "#64748b" }}>
                    OR
                  </span>
                </div>

                <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                    <div style={{ position: "relative", display: "inline-block", width: "40px", height: "20px" }}>
                      <input
                        type="checkbox"
                        checked={showMonthsSelection}
                        onChange={(e) => {
                          setShowMonthsSelection(e.target.checked);
                          if (!e.target.checked) {
                            setForm({ ...form, activeMonths: [] });
                          }
                        }}
                        style={{ display: "none" }}
                      />
                      <div style={{
                        width: "40px",
                        height: "20px",
                        background: showMonthsSelection ? "#76AB3F" : "#cbd5e1",
                        borderRadius: "10px",
                        transition: "background 0.2s",
                        position: "relative",
                        cursor: "pointer"
                      }}>
                        <div style={{
                          position: "absolute",
                          top: "2px",
                          left: showMonthsSelection ? "22px" : "2px",
                          width: "16px",
                          height: "16px",
                          background: "white",
                          borderRadius: "50%",
                          transition: "left 0.2s"
                        }} />
                      </div>
                    </div>
                    <span style={{ fontSize: "12px", fontWeight: 800, color: darkMode ? "#d1d5db" : "#475569", textTransform: "uppercase" }}>
                      SELECT MONTHS <span style={{ fontWeight: 500, textTransform: "none", color: darkMode ? "#9ca3af" : "#64748b" }}>(for Monthly & Yearly)</span>
                    </span>
                  </label>

                  {/* Months selection grid */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gap: "10px",
                    marginTop: "12px",
                    opacity: showMonthsSelection ? 1 : 0.5,
                    pointerEvents: showMonthsSelection ? "auto" : "none",
                    transition: "opacity 0.2s"
                  }}>
                    {months.map((month, index) => {
                      const monthNumber = index + 1;
                      const active = form.activeMonths?.includes(monthNumber);

                      return (
                        <button
                          key={month}
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              activeMonths: active
                                ? prev.activeMonths.filter((m) => m !== monthNumber)
                                : [...(prev.activeMonths || []), monthNumber],
                            }))
                          }
                          style={{
                            height: "46px",
                            borderRadius: "10px",
                            border: active
                              ? "1.5px solid #76AB3F"
                              : darkMode
                                ? "1px solid #374151"
                                : "1px solid #cbd5e1",
                            background: active
                              ? (darkMode ? "rgba(118, 171, 63, 0.15)" : "#f1f8ea")
                              : (darkMode ? "#111827" : "white"),
                            color: active
                              ? "#76AB3F"
                              : (darkMode ? "#9ca3af" : "#64748b"),
                            fontWeight: 700,
                            cursor: readOnly || !isMainEditable || !showMonthsSelection ? "default" : "pointer",
                            fontSize: "13px",
                            transition: "all 0.2s",
                            boxSizing: "border-box"
                          }}
                          disabled={readOnly || !isMainEditable || !showMonthsSelection}
                        >
                          {month}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Bottom info banner */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: darkMode ? "#1e293b" : "#f0f7ff",
                    border: darkMode ? "1px solid #334155" : "1px solid #e0f2fe",
                    borderRadius: "12px",
                    padding: "12px 16px",
                    color: darkMode ? "#38bdf8" : "#0284c7",
                    marginTop: "8px"
                  }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span style={{ fontSize: "13px", fontWeight: 600 }}>
                    Task will be created based on the above schedule.
                  </span>
                </div>
              </div>
            )}
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <div style={sectionBarStyle} />
              <h4 style={sectionHeadingStyle}>Issues</h4>
            </div>

            <div className="task-form-issues-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div className="form-group">
                <label style={labelStyle}>Issue Title</label>
                <input
                  name="issueTitle"
                  placeholder="Enter issue title"
                  value={form.issueTitle}
                  onChange={handleChange}
                  disabled={readOnly || !isMainEditable}
                  style={inputStyle}
                />
              </div>

              <div className="form-group">
                <label style={labelStyle}>Issue Description</label>
                <div style={{ position: "relative" }}>
                  <textarea
                    name="issueDescription"
                    placeholder="Enter issue description"
                    value={form.issueDescription}
                    onChange={handleChange}
                    disabled={readOnly || !isMainEditable}
                    style={{
                      ...inputStyle,
                      minHeight: "80px",
                      resize: "vertical",
                      paddingRight: "40px"
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "12px",
                      right: "12px",
                      pointerEvents: "none",
                      color: darkMode ? "#9ca3af" : "#64748b"
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <div style={sectionBarStyle} />
              <h4 style={sectionHeadingStyle}>File Upload</h4>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                gap: "16px"
              }}
            >
              {form.attachments.map((att, i) => (
                <div
                  key={`exist-${i}`}
                  style={{
                    border: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
                    borderRadius: "16px",
                    padding: "8px",
                    background: darkMode ? "#111827" : "#f8fafc",
                    position: "relative"
                  }}
                >
                  <div
                    onClick={() =>
                      att.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) &&
                      setSelectedImage(att.url)
                    }
                    style={{
                      height: "100px",
                      borderRadius: "12px",
                      background: att.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                        ? `url(${att.url}) center/cover`
                        : darkMode
                          ? "#374151"
                          : "#e2e8f0",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {!att.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                      <span
                        style={{
                          fontSize: "10px",
                          color: darkMode ? "#9ca3af" : "#64748b",
                          fontWeight: 800
                        }}
                      >
                        FILE
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginTop: "4px",
                      color: darkMode ? "#9ca3af" : "#64748b",
                      fontWeight: 600
                    }}
                  >
                    {att.name}
                  </div>

                  {canEditTroubleFields && (
                    <span
                      onClick={() => removeAttachment(i)}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: darkMode
                          ? "rgba(31, 41, 55, 0.9)"
                          : "rgba(255,255,255,0.9)",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ef4444",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                    >
                      ×
                    </span>
                  )}
                </div>
              ))}

              {selectedFiles.map((file, i) => (
                <div
                  key={`new-${i}`}
                  style={{
                    border: darkMode ? "1px solid #4d7a22" : "1px solid #76AB3F",
                    borderRadius: "16px",
                    padding: "8px",
                    background: darkMode ? "#1e293b" : "#F0F8E9",
                    position: "relative"
                  }}
                >
                  <div
                    style={{
                      height: "100px",
                      borderRadius: "12px",
                      background: file.type.startsWith("image/")
                        ? `url(${URL.createObjectURL(file)}) center/cover`
                        : darkMode
                          ? "#374151"
                          : "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {!file.type.startsWith("image/") && (
                      <span
                        style={{
                          fontSize: "10px",
                          color: darkMode ? "#4ade80" : "#76AB3F",
                          fontWeight: 800
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginTop: "4px",
                      color: darkMode ? "#4ade80" : "#76AB3F",
                      fontWeight: 600
                    }}
                  >
                    {file.name}
                  </div>

                  <span
                    onClick={() => removeSelectedFile(i)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: darkMode
                        ? "rgba(31, 41, 55, 0.9)"
                        : "rgba(255,255,255,0.9)",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ef4444",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "bold",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}

              {canEditTroubleFields && (
                <label
                  style={{
                    border: darkMode ? "2px dashed #4b5563" : "2px dashed #cbd5e1",
                    borderRadius: "16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100px",
                    height: "100px",
                    cursor: "pointer",
                    color: darkMode ? "#9ca3af" : "#64748b",
                    boxSizing: "border-box"
                  }}
                >
                  <span style={{ fontSize: "24px", fontWeight: "bold", lineHeight: 1 }}>+</span>
                  <span style={{ fontSize: "11px", fontWeight: 700, marginTop: "2px" }}>UPLOAD</span>
                  <input id="fileInput-1" name="fileInput-1"
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    style={{ display: "none" }}
                  />
                </label>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "16px",
              paddingTop: "6px"
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "12px 28px",
                borderRadius: "10px",
                background: darkMode ? "#374151" : "white",
                color: darkMode ? "#d1d5db" : "#64748b",
                fontWeight: 800,
                border: darkMode ? "1px solid #4b5563" : "1px solid #e2e8f0",
                cursor: "pointer"
              }}
            >
              {readOnly ? "CLOSE" : "CANCEL"}
            </button>

            {(!readOnly || troubleOnlyMode) && (
              <button
                type="submit"
                disabled={uploading}
                style={{
                  padding: "12px 36px",
                  borderRadius: "10px",
                  background: "#76AB3F",
                  color: "white",
                  fontWeight: 800,
                  border: "none",
                  cursor: uploading ? "not-allowed" : "pointer",
                  opacity: uploading ? 0.7 : 1,
                  boxShadow: darkMode
                    ? "0 8px 16px rgba(0,0,0,0.4)"
                    : "0 8px 16px rgba(118,171,63,0.25)"
                }}
              >
                {uploading ? "SYNCING..." : "SUBMIT"}
              </button>
            )}
          </div>
        </form>

        {selectedImage && (
          <div
            className="modal-backdrop"
            onClick={() => setSelectedImage(null)}
            style={{
              zIndex: 3000,
              background: "rgba(0,0,0,0.85)",
              backdropFilter: "blur(8px)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh"
              }}
            >
              <button
                onClick={() => setSelectedImage(null)}
                style={{
                  position: "absolute",
                  top: -40,
                  right: 0,
                  background: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  cursor: "pointer"
                }}
              >
                ✕
              </button>

              <img
                src={selectedImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  borderRadius: "12px",
                  display: "block"
                }}
              />
            </div>
          </div>
        )}

        {showLoopPreview && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                width: "1320px",
                maxWidth: "96vw",
                height: "92vh",
                maxHeight: "92vh",
                overflow: "hidden",
                background: "#ffffff",
                borderRadius: "18px",
                padding: "24px",
                boxShadow: "0 25px 60px rgba(0,0,0,0.22)",
                color: "#0f172a",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                    Recurring Task Calendar Preview
                  </h2>
                  <p style={{ margin: "6px 0 0", color: "#64748b", fontWeight: 400 }}>
                    Preview of tasks that will be created based on your loop configuration.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowLoopPreview(false)}
                  style={{
                    width: 44,
                    height: 44,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                    borderRadius: 12,
                    fontSize: 24,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 14,
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 14,
                  marginBottom: 18,
                }}
              >
                {[
                  ["Frequency", form.repeatFrequency],
                  ["Every", `${form.repeatEvery} ${getRepeatUnitLabel()}`],
                  ["Duration", `${form.loopDurationValue} ${form.loopDurationUnit}`],
                  ["Occurrences", previewInstances.length],
                  ["Start Date", formatPreviewDate(form.repeatStartDate)],
                  ["End Date", formatPreviewDate(previewInstances[previewInstances.length - 1]?.endDate)],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: "#f8fafc", borderRadius: 12, padding: 14 }}>
                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{label}</div>
                    <div style={{ marginTop: 6, fontSize: 15, fontWeight: 600 }}>{value}</div>
                  </div>
                ))}
              </div>

              {previewInstances.length === 0 && (
                <div style={{
                  background: "#fef2f2",
                  border: "1px solid #fee2e2",
                  color: "#991b1b",
                  padding: "14px 18px",
                  borderRadius: 12,
                  marginBottom: 18,
                  fontWeight: 600,
                  fontSize: 14,
                }}>
                  ⚠️ Unable to generate loop preview. Please verify start date, end date, frequency, active months, and skip weekend settings.
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 360px",
                  gap: 18,
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    padding: 14,
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <div
                    style={{
                      minWidth: 1050,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button type="button" onClick={() => movePreviewMonth(-1)} style={navBtnStyle}>‹</button>
                        <button type="button" onClick={() => movePreviewMonth(1)} style={navBtnStyle}>›</button>
                        <button type="button" onClick={goPreviewToday} style={navTextBtnStyle}>Today</button>
                      </div>

                      <h3 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{previewMonthLabel}</h3>

                      <div style={{ display: "flex", gap: 10 }}>
                        <button type="button" style={{ ...navTextBtnStyle, background: "#111827", color: "white", fontWeight: 600 }}>
                          Month
                        </button>

                        <select
                          value={previewMonth.slice(0, 4)}
                          onChange={(e) => setPreviewMonth(`${e.target.value}-${previewMonth.slice(5, 7)}`)}
                          style={{ ...navTextBtnStyle, fontWeight: 600 }}
                        >
                          {Array.from({ length: 10 }, (_, i) => {
                            const y = new Date().getFullYear() - 2 + i;
                            return <option key={y} value={y}>{y}</option>;
                          })}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                        <div key={d} style={{ textAlign: "center", fontWeight: 600, padding: "10px" }}>
                          {d}
                        </div>
                      ))}

                      {buildPreviewCalendar().map((cell) => {
                        const tasks = getTasksForPreviewDate(cell.iso);

                        return (
                          <div
                            key={cell.iso}
                            style={{
                              height: 112,
                              border: "1px solid #e5e7eb",
                              padding: 10,
                              background: cell.inMonth ? "#ffffff" : "#f8fafc",
                              opacity: cell.inMonth ? 1 : 0.45,
                              boxSizing: "border-box",
                              overflow: "hidden",
                            }}
                          >
                            <div style={{ fontWeight: 500, fontSize: 14 }}>{cell.day}</div>

                            <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 7 }}>
                              {tasks.map((task) => (
                                <div
                                  key={`${task.id}-${cell.iso}`}
                                  style={{
                                    background: "#ffedd5",
                                    color: "#9a3412",
                                    border: "1px solid #fed7aa",
                                    borderRadius: 8,
                                    padding: "8px 10px",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    lineHeight: 1.2,
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                               >
                                  {form.title || "New Recurring Task"}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>
                    Generated Instances ({previewInstances.length})
                  </div>

                  <div
                    style={{
                      padding: 14,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      overflowY: "auto",
                      minHeight: 0,
                    }}
                  >
                    {previewInstances.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom: "1px solid #f1f5f9",
                          paddingBottom: 10,
                          fontWeight: 400,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 8,
                              background: "#eef8e8",
                              color: "#3f7d20",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 600,
                            }}
                          >
                            {item.id}
                          </span>

                          <span style={{ fontWeight: 400 }}>
                            {formatPreviewDate(item.startDate)} - {formatPreviewDate(item.endDate)}
                          </span>
                        </div>

                        <span
                          style={{
                            background: "#eef8e8",
                            color: "#3f7d20",
                            borderRadius: 999,
                            padding: "5px 10px",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {form.loopDurationValue} {form.loopDurationUnit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 14,
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowLoopPreview(false)}
                  style={{
                    background: "#4f46e5",
                    color: "white",
                    border: "none",
                    borderRadius: 10,
                    padding: "13px 56px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 15,
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TaskForm;
